/* eslint-disable no-unused-vars */
import React, { useEffect, useCallback, useState } from 'react';
import { Button, message, Modal, Space, Switch, Tag, Tooltip } from 'antd';
import useListFilters from '../../hooks/useListFilters';
import ListPage from '../../components/core/ListPage';
import {
  getInvoiceEmailSentStatusTagColor,
  getInvoicePaymentStatusTagColor,
} from '../../utils/clinic';
import { useNavigate } from 'react-router-dom';
import { getMonthTitleFromMonthNumber } from '../../utils/date';
import { useRadiologistInvoiceList } from '../../hooks/radiologists/useRadiologistInvoiceList';
import SelectInvoiceDurationForm from '../../components/invoices/SelectInvoiceDurationForm';
import { useCreateCustomDurationInvoices } from '../../hooks/radiologists/useCreateCustomDurationInvoices';
import RadiologistInvoicePreviewDownloader from '../../components/invoices/RadiologistInvoicePreviewDownloader';
import RadiologistInvoiceEmailSender from '../../components/invoices/RadiologistInvoiceEmailSender';
import { useRadiologistInvoiceUpdate } from '../../hooks/radiologists/useRadiologistInvoiceUpdate';
const BREAD_CRUMBS = [
  { label: 'Radiologist', to: '/user/radiologistlist' },
  { label: 'Revenue List', to: '/user/radiologist-invoice-list' },
];

export const RadiologistInvoiceList = () => {
  const [open, setOpen] = useState(false);
  const { data, getList } = useRadiologistInvoiceList();
  const { createCustomDurationInvoices } =
    useCreateCustomDurationInvoices();
  const { loading: updatingPaymentStatus, updateInvoicePaymentStatus } = useRadiologistInvoiceUpdate();
  const {
    search,
    setSearch,
    pagination,
    setPagination,
    sortParams,
    setSortParams,
    loading,
    setLoading,
    selectedRows,
    setSelectedRows,
  } = useListFilters();
  const navigate = useNavigate();

  // Table
  const handleEdit = (record) => {
    navigate(`/user/radiologist-invoice/edit?id=${record?.invoiceId}`);
  };
  const handleGenerateMontlyInvoices = () => setOpen(true);

  // Modal
  const handleCancel = () => setOpen(false);
  const handleSubmit = async (values) => {
    try {
      handleCancel();
      if (!values) return;
      await createCustomDurationInvoices(values);
      reload();
    } catch (error) {
      console.log('Error on create monthly case invoices');
    }
  };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getList({
        search,
        page: pagination.current,
        limit: pagination.pageSize,
        sortOrder: sortParams.sortOrder,
        sortBy: sortParams.sortField,
      });
      setLoading(false);
      setPagination({ total: response.totalItems });
    } catch (error) {
      setLoading(false);
    }
  }, [
    pagination.current,
    pagination.pageSize,
    sortParams.sortOrder,
    sortParams.sortField,
    search,
  ]);
  const handleEmailSent = () => reload();

  const handleTableChange = (newPagination, _, sorter) => {
    const pageSizeChanged = newPagination?.pageSize !== pagination.pageSize;
    const nextPage = pageSizeChanged ? 1 : newPagination?.current || 1;
    setSortParams({
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
    setPagination({ current: nextPage, pageSize: newPagination?.pageSize });
  };

  const handleSearch = (searchValue) => {
    setSearch(searchValue);
  };

  useEffect(() => {
    reload();
  }, [
    pagination.current,
    pagination.pageSize,
    sortParams.sortOrder,
    sortParams.sortField,
    search,
  ]);

  const handlePaymentStatusUpdate = (invoiceId, checked) => {
    console.log("checked : ", checked);
    Modal.confirm({
      title: 'Update Payment Status',
      content: 'Are you sure you want to update the payment status?',
      onOk: async () => {
        try {
          await updateInvoicePaymentStatus(invoiceId, { paymentStatus: checked });
          message.success("Invoice payment status updated successfully");
          reload();
        } catch (error) {
          console.log('Error on update payment status');
          message.error("Failed to update payment status");
        }
      },
    });
  };

  const columns = [
    {
      title: 'Invoice No.',
      dataIndex: 'invoiceNo',
      sorter: true,
      render: (text) => {
        if (!text) return text;
        const value = text.toString();
        const parts = value.split('-');
        return parts.length > 1 ? parts.slice(1).join('-') : value;
      },
    },
    {
      title: 'Radiologist Name',
      dataIndex: 'radiologistName',
      sorter: true,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: 'Revenue Amount',
      dataIndex: 'amount',
    },
    {
      title: 'Month-Year',
      dataIndex: 'monthNumber',
      sorter: true,
      render: (_, { monthNumber, yearNumber }) => {
        if (monthNumber < 0 || !yearNumber) {
          return undefined;
        }
        const date = new Date();
        date.setMonth(monthNumber);
        const monthTitle = date.toLocaleString('en-US', { month: 'short' });
        return `${monthTitle}-${yearNumber}`;
      },
    },
    {
      title: 'Payment Status',
      dataIndex: 'paymentId',
      render: (_, { paymentStatus, invoiceId }, index) => {
        let statusMeta = getInvoicePaymentStatusTagColor(paymentStatus);
        return (
          <Switch
            value={statusMeta?.isPaid}
            checkedChildren={statusMeta?.text}
            unCheckedChildren={statusMeta?.text}
            onChange={(checked) => handlePaymentStatusUpdate(invoiceId, checked)}
          >
          </Switch>
        );
      },
    },
    {
      title: 'Send Status',
      dataIndex: 'emailedStatus',
      render: (_, { emailedStatus }, index) => {
        let statusMeta = getInvoiceEmailSentStatusTagColor(emailedStatus);
        return (
          <Tag color={statusMeta?.color} key={index}>
            {statusMeta?.text}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: '',
      render: (_, record) => {
        return (
          <Space size="middle">
            {record?.paymentId ? null : (
              <Tooltip title="Edit Invoice" placement="left">
                <Button
                  icon={<i className="fa fa-edit" />}
                  onClick={() => handleEdit(record)}
                ></Button>
              </Tooltip>
            )}
            <RadiologistInvoicePreviewDownloader
              invoiceId={record?.invoiceId}
              invoiceNo={record?.invoiceNo}
            />
            <RadiologistInvoiceEmailSender
              invoiceId={record?.invoiceId}
              onEmailSent={handleEmailSent}
            />
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <ListPage
        title={'Revenue List'}
        data={data?.items || []}
        columns={columns}
        loading={loading || updatingPaymentStatus}
        pagination={pagination}
        rowKey={(record) => record?.invoiceId}
        onRefresh={reload}
        onSearch={handleSearch}
        onTableChange={handleTableChange}
        breadCrumbItems={BREAD_CRUMBS}
        hideAddAction
        showCheckbox={false}
        onSelectRowsfun={setSelectedRows}
      />
      <Modal
        title={'Select Invoice Duration'}
        open={open}
        onCancel={handleCancel}
        maskClosable={false}
        footer={null}
        width={300}
      >
        <SelectInvoiceDurationForm
          onCancel={handleCancel}
          onSubmit={handleSubmit}
        />
      </Modal>
    </>
  );
};
